import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {PorQue} from '@/components/ui/por-que'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {MotivoDialog} from '@/components/motivo-dialog'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {HelpIcon} from '@/components/help-icon'
import {
  BANDEJA_PAGE_SIZE,
  HISTORIAL_PAGE_SIZE,
  useCambiosOcupacion,
  useConfirmarCambio,
  useDescartarCambio,
  useHistorialCambios,
  useResumenCambios,
  type SugerenciaCambio,
} from '@/hooks/salud/medicina_laboral/use-cambio-ocupacion'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'

/** `sin_periodicidad` va neutral: la Res. 1843 no da plazo, así que sin la cifra de la empresa
 * no hay fecha contra la cual estar vencido. */
function casillaTone(color: string): 'amber' | 'red' | 'neutral' {
  if (color === 'rojo') return 'red'
  return color === 'amarillo' ? 'amber' : 'neutral'
}

function Restas({sugerencia}: {sugerencia: SugerenciaCambio}) {
  const {t} = useTranslation()
  return (
    <div className="mt-1 space-y-0.5" data-testid={`cambio-restas-${sugerencia.id}`}>
      {sugerencia.peligros_nuevos.map((peligro) => (
        <p key={`nuevo-${peligro}`} className="text-xs text-foreground">
          {t('cambioOcupacion.peligroNuevo', {peligro})}
        </p>
      ))}
      {sugerencia.peligros_subieron.map((subido) => (
        <p key={`subio-${subido.texto}`} className="text-xs text-foreground">
          {t('cambioOcupacion.peligroSubio', {
            peligro: subido.texto,
            antes: subido.nivel_anterior,
            despues: subido.nivel_nuevo,
          })}
        </p>
      ))}
      {sugerencia.condiciones_nuevas.map((condicion) => (
        <p key={`condicion-${condicion}`} className="text-xs text-foreground">
          {t('cambioOcupacion.condicionNueva', {
            // Las condiciones cerradas viajan como código y se dicen con el nombre de la casa;
            // el renglón libre viaja como texto.
            condicion: t(`condicionCargo.codigo.${condicion}`, {defaultValue: condicion}),
          })}
        </p>
      ))}
    </div>
  )
}

/** Descartar pide motivo escrito: queda en la historia de la persona. */
function DescartarDialog({businessId, sugerencia}: {businessId: string; sugerencia: SugerenciaCambio}) {
  const {t} = useTranslation()
  const descartar = useDescartarCambio(businessId)
  return (
    <MotivoDialog
      triggerLabel={t('cambioOcupacion.descartar.action')}
      triggerTestId={`cambio-descartar-${sugerencia.id}`}
      title={t('cambioOcupacion.descartar.title')}
      description={t('cambioOcupacion.descartar.description')}
      motivoLabel={t('cambioOcupacion.descartar.motivoLabel')}
      motivoTestId="cambio-descartar-motivo"
      submitLabel={t('cambioOcupacion.descartar.action')}
      onSubmit={(motivo) => descartar.mutateAsync({sugerenciaId: sugerencia.id, motivo})}
    />
  )
}

/** Nombra solo el cargo que existe y no tiene perfil; en `sin_comparar` `perfil_nuevo_id` siempre es nulo. */
function SinComparar({businessId, sugerencia}: {businessId: string; sugerencia: SugerenciaCambio}) {
  const {t} = useTranslation()
  const faltaElAnterior = sugerencia.perfil_anterior_id === null
  const cargo = faltaElAnterior ? sugerencia.position_anterior : sugerencia.position_nueva
  const cargoId = faltaElAnterior ? sugerencia.position_anterior_id : sugerencia.position_nueva_id
  const vigente = faltaElAnterior ? sugerencia.position_anterior_vigente : sugerencia.position_nueva_vigente
  return (
    <p
      className="flex flex-wrap items-center gap-1.5 text-xs text-foreground"
      data-testid={`cambio-sin-comparar-${sugerencia.id}`}>
      {t('cambioOcupacion.sinComparar', {cargo})}
      {vigente ? (
        <Link className="underline" to={`/businesses/${businessId}/cargos/${cargoId}/perfil`}>
          {t('cambioOcupacion.verElCargo')}
        </Link>
      ) : (
        <span data-testid={`cambio-cargo-eliminado-${sugerencia.id}`}>{t('cambioOcupacion.cargoEliminado')}</span>
      )}
      <PorQue label={t('common.porQue')} testId={`cambio-sin-comparar-porque-${sugerencia.id}`}>
        <span>{t('cambioOcupacion.sinCompararPorque')}</span>
      </PorQue>
    </p>
  )
}

function Fila({
  businessId,
  sugerencia,
  puedeDecidir,
  plazoDias,
}: {
  businessId: string
  sugerencia: SugerenciaCambio
  puedeDecidir: boolean
  plazoDias: number | null
}) {
  const {t} = useTranslation()
  const confirmar = useConfirmarCambio(businessId)
  const [error, setError] = useState<string | null>(null)
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3" data-testid={`cambio-${sugerencia.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">
          {t('cambioOcupacion.fila', {
            nombre: sugerencia.employee_nombre,
            anterior: sugerencia.position_anterior,
            nueva: sugerencia.position_nueva,
            fecha: formatDateOnly(sugerencia.fecha_cambio),
          })}
        </p>
        {sugerencia.estado === 'sin_comparar' ? (
          <SinComparar businessId={businessId} sugerencia={sugerencia} />
        ) : sugerencia.estado === 'no_exigido' ? (
          <p
            className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
            data-testid={`cambio-no-exigido-${sugerencia.id}`}>
            {t('cambioOcupacion.noExigido')}
            <PorQue label={t('common.porQue')} testId={`cambio-no-exigido-porque-${sugerencia.id}`}>
              <span>{t('cambioOcupacion.noExigidoPorque')}</span>
            </PorQue>
          </p>
        ) : (
          <Restas sugerencia={sugerencia} />
        )}
        {sugerencia.descartado_motivo && (
          <p className="text-xs text-muted-foreground" data-testid={`cambio-descartado-${sugerencia.id}`}>
            {t('cambioOcupacion.descartado', {
              motivo: sugerencia.descartado_motivo,
              nombre: sugerencia.decidido_por_nombre ?? '',
            })}
          </p>
        )}
        {sugerencia.casilla && sugerencia.casilla.color === 'sin_periodicidad' && (
          <p className="text-xs text-muted-foreground" data-testid={`cambio-sin-plazo-${sugerencia.id}`}>
            {t('cambioOcupacion.plazoSinDefinir')}{' '}
            <Link className="underline" to={`/businesses/${businessId}/cifras`}>
              {t('cambioOcupacion.fijarPlazo')}
            </Link>
          </p>
        )}
        {sugerencia.casilla?.vence_el && (
          <p className="font-mono text-[11px] text-muted-foreground" data-testid={`cambio-vence-${sugerencia.id}`}>
            {t('cambioOcupacion.venceEl', {fecha: formatDateOnly(sugerencia.casilla.vence_el), dias: plazoDias})}
          </p>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
      <div className="flex items-center gap-2">
        <Tag
          tone={sugerencia.casilla ? casillaTone(sugerencia.casilla.color) : 'neutral'}
          data-testid={`cambio-estado-${sugerencia.id}`}>
          {t(`cambioOcupacion.estado.${sugerencia.estado}`)}
        </Tag>
        {puedeDecidir && sugerencia.estado === 'sugerida' && (
          <>
            <Button
              size="sm"
              data-testid={`cambio-confirmar-${sugerencia.id}`}
              disabled={confirmar.isPending}
              onClick={async () => {
                setError(null)
                try {
                  await confirmar.mutateAsync({sugerenciaId: sugerencia.id})
                } catch (err) {
                  setError(translateApiError(err))
                }
              }}>
              {t('cambioOcupacion.confirmar')}
            </Button>
            <DescartarDialog businessId={businessId} sugerencia={sugerencia} />
          </>
        )}
      </div>
    </li>
  )
}

/** E1-H11, D-S. Nada se pinta rojo hasta que alguien confirma; el corte es propuesta de Inci,
 * no de la Res. 1843. */
function Historial({businessId, puedeDecidir}: {businessId: string; puedeDecidir: boolean}) {
  const {t} = useTranslation()
  const [abierto, setAbierto] = useState(false)
  const [page, setPage] = useState(1)
  const {data} = useHistorialCambios(businessId, abierto, page)
  return (
    <div className="space-y-2">
      <Button variant="ghost" size="sm" data-testid="cambios-historial-toggle" onClick={() => setAbierto(!abierto)}>
        {t(abierto ? 'cambioOcupacion.ocultarHistorial' : 'cambioOcupacion.verHistorial')}
      </Button>
      {abierto && data && (
        <>
          <ul className="divide-y divide-border" data-testid="cambios-historial">
            {data.items.length === 0 ? (
              <li className="py-3 text-xs text-muted-foreground">{t('cambioOcupacion.historialVacio')}</li>
            ) : (
              data.items.map((sugerencia) => (
                <Fila
                  key={sugerencia.id}
                  businessId={businessId}
                  sugerencia={sugerencia}
                  puedeDecidir={puedeDecidir}
                  plazoDias={null}
                />
              ))
            )}
          </ul>
          {data.total > 0 && (
            <PaginationBar page={page} pageSize={HISTORIAL_PAGE_SIZE} total={data.total} onPageChange={setPage} />
          )}
        </>
      )}
    </div>
  )
}

export function CambioOcupacionSection({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const capabilities = useCapabilities(businessId)
  const [page, setPage] = useState(1)
  const {data, isLoading, isError, error} = useCambiosOcupacion(businessId, page)
  // El plazo es de la empresa: viaja en el mismo resumen que alimenta el chip de la pestaña.
  const resumen = useResumenCambios(businessId)
  const puedeDecidir = capabilities.sgsst_firmar && !godReadOnly
  if (isLoading && !data) return <Skeleton className="h-16 w-full" />
  return (
    <section className="surface space-y-2 rounded-lg p-4" data-testid="cambios-ocupacion">
      {/* Sin <h3>: la sección es la pestaña «Sugerencias» (R7). */}
      <div className="flex justify-end">
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('examenesMatriz.tabs.sugerencias')})}
          testId="cambio-corte-de-inci">
          {t('examenes.cambioOcupacionCorte')}
        </HelpIcon>
      </div>
      {/* Un 403 o un 500 se dice: «no pude leer» no es «no hay nada». */}
      {isError ? (
        <p className="text-sm text-destructive" data-testid="cambios-ocupacion-error">
          {translateApiError(error)}
        </p>
      ) : !data ? null : (
        <>
          {data.items.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="cambios-ocupacion-vacio">
              {t('cambioOcupacion.sinCambios')}
            </p>
          ) : (
            <>
              <ul className="divide-y divide-border">
                {data.items.map((sugerencia) => (
                  <Fila
                    key={sugerencia.id}
                    businessId={businessId}
                    sugerencia={sugerencia}
                    puedeDecidir={puedeDecidir}
                    plazoDias={resumen.data?.plazo_dias ?? null}
                  />
                ))}
              </ul>
              <PaginationBar page={page} pageSize={BANDEJA_PAGE_SIZE} total={data.total} onPageChange={setPage} />
            </>
          )}
          <Historial businessId={businessId} puedeDecidir={puedeDecidir} />
        </>
      )}
    </section>
  )
}
